import { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, matchPath, useLocation } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import { ProtectedRoute, PathwayGuard, routes } from "routes";
import NotFound from "views/errors/404";
import { AIChatButton, AIChatDrawer } from "components/ai-chat";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useAuth } from "hooks/useAuth";

const XL_BREAKPOINT = 1200;

/**
 * Trail of matched admin routes for `pathname`, outermost first. A collapse
 * route matches as a prefix; when none of its items match, the module itself
 * is the last crumb.
 */
function findRouteTrail(list, pathname, trail = []) {
  let fallback = null;
  for (const route of list) {
    const fullPath = `${route.layout}${route.path}`;
    if (route.collapse) {
      const match = matchPath({ path: fullPath, end: false }, pathname);
      if (!match) continue;
      const here = [...trail, { name: route.name, path: match.pathname, route }];
      const childTrail = findRouteTrail(route.items, pathname, here);
      if (childTrail) return childTrail;
      fallback = fallback || here;
      continue;
    }
    const match = matchPath(fullPath, pathname);
    if (match) return [...trail, { name: route.name, path: match.pathname, route }];
  }
  return fallback;
}

function renderRoutes() {
  const collect = (acc, route) => {
    if (route.layout === "/admin") {
      acc.push(route);
      if (route.collapse) route.items.reduce(collect, acc);
    }
    return acc;
  };

  return routes.reduce(collect, []).map((route, key) => {
    const element = route.path.includes("pathways") ? (
      <ProtectedRoute>{route.component}</ProtectedRoute>
    ) : (
      <ProtectedRoute>
        <PathwayGuard>{route.component}</PathwayGuard>
      </ProtectedRoute>
    );
    return <Route path={route.path} element={element} key={key} />;
  });
}

export default function Admin() {
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= XL_BREAKPOINT);
  const [open, setOpen] = useState(isDesktop);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [collapsed, setCollapsed] = useLocalStorage("atrium-sidebar-collapsed", false);
  const { user } = useAuth();
  const isPathway = user?.customerStatus === "pathway";
  const railCollapsed = collapsed && isDesktop;

  const trail = useMemo(
    () => findRouteTrail(routes, location.pathname) || [],
    [location.pathname]
  );
  const breadcrumb = useMemo(() => trail.map(({ name, path }) => ({ name, path })), [trail]);
  const isFullscreen = !!trail[trail.length - 1]?.route.fullscreen;

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= XL_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setOpen(isDesktop);
  }, [isDesktop]);

  // The drawer covers the page below xl, so close it after every navigation.
  useEffect(() => {
    if (window.innerWidth < XL_BREAKPOINT) setOpen(false);
  }, [location.pathname]);

  const pageRoutes = (
    <Routes>
      {renderRoutes()}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate to="/admin/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  if (isFullscreen) {
    return <div className="min-h-screen w-full bg-canvas font-dm dark:text-white">{pageRoutes}</div>;
  }

  return (
    <div className="flex min-h-screen w-full bg-canvas font-dm dark:text-white">
      {open && (
        <div
          className="fixed inset-0 z-40 bg-overlay/60 xl:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      <Sidebar
        open={open}
        onClose={() => setOpen(false)}
        collapsed={railCollapsed}
        onToggleCollapse={() => setCollapsed(!collapsed)}
      />
      <div
        className={`flex min-h-screen min-w-0 flex-1 flex-col transition-[margin] duration-300 ease-in-out ${
          railCollapsed ? "xl:ml-[76px]" : "xl:ml-[260px]"
        }`}
      >
        <Navbar onOpenSidenav={() => setOpen(!open)} breadcrumb={breadcrumb} />
        <main className="flex flex-1 flex-col px-4 pb-10 md:px-6">{pageRoutes}</main>
        <Footer />
      </div>

      {/* AI Chat — hidden for pathway (free tier) customers */}
      {!isPathway && (
        <>
          <AIChatButton onClick={() => setIsChatOpen(true)} isOpen={isChatOpen} />
          <AIChatDrawer isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
        </>
      )}
    </div>
  );
}
